import debounce from "lodash/debounce";
import { useEffect, useState, useMemo } from "react";

interface Breakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

const breakpoints = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1170,
} satisfies Breakpoints;

export const useMediaQuery = (size: keyof Breakpoints): boolean => {
  const [isMatch, setIsMatch] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Handle SSR
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const mediaQuery = useMemo(() => {
    if (typeof window === "undefined") return null;
    return window.matchMedia(`(max-width: ${breakpoints[size]}px)`);
  }, [size]);

  useEffect(() => {
    if (!isMounted || !mediaQuery) return;

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMatch(e.matches);
    };

    // Set initial value
    setIsMatch(mediaQuery.matches);

    // Add event listener
    mediaQuery.addEventListener("change", debounce(handleChange, 200));

    return () => {
      mediaQuery.removeEventListener("change", debounce(handleChange, 200));
    };
  }, [mediaQuery, isMounted]);

  // Return false during SSR
  if (!isMounted) return false;

  return isMatch;
};

export default useMediaQuery;
